/**
 * R.H.I.E. Provider Component
 * P2 - AI Öneri, Drop Risk, Seviye Bazlı Rehber Entegrasyonu
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { rhieEngine } from '../../services/RHIEEngine';
import { rhieEventBus, RHIE_EVENTS } from '../../services/zihinKilavuzuEventBus';
import RHIEToast from './RHIEToast';
import RHIEConfetti from './RHIEConfetti';

const RHIEContext = createContext(null);

export function RHIEProvider({ children }) {
  const { token, user } = useAuth();
  
  // Core state
  const [initialized, setInitialized] = useState(false);
  const [dailyRitual, setDailyRitual] = useState(null);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [todayCompleted, setTodayCompleted] = useState(false);
  
  // P2 State: AI & Drop Risk
  const [dropRiskScore, setDropRiskScore] = useState(0);
  const [dropRiskLevel, setDropRiskLevel] = useState('LOW');
  const [recommendations, setRecommendations] = useState([]);
  const [userLevel, setUserLevel] = useState('Farkındalık');
  const [guideIntensity, setGuideIntensity] = useState(null);
  
  // Toast state
  const [toast, setToast] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Initialize engine when auth changes
  useEffect(() => {
    if (token && user) {
      rhieEngine.initialize(token, user).then(() => {
        setInitialized(true);
        setDailyRitual(rhieEngine.getDailyRitual());
        setCurrentStreak(rhieEngine.getCurrentStreak());
        setTodayCompleted(rhieEngine.isTodayCompleted());
        setDropRiskScore(rhieEngine.getDropRiskScore());
        setDropRiskLevel(rhieEngine.getDropRiskLevel());
        setRecommendations(rhieEngine.getPersonalizedRecommendations());
        setUserLevel(rhieEngine.getUserLevel());
        setGuideIntensity(rhieEngine.getGuideIntensity());
      });
    }
  }, [token, user]);
  
  // Listen for events
  useEffect(() => {
    const unsubscribes = [];
    
    // Ritual completed - show toast + confetti
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.RITUAL_COMPLETED, (data) => {
        setTodayCompleted(true);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
        
        // Update streak from engine
        setCurrentStreak(rhieEngine.getCurrentStreak());
      })
    );
    
    // Streak extended
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.STREAK_EXTENDED, (data) => {
        setCurrentStreak(data.currentStreak);
      })
    );
    
    // Dönüşüm Adımı - extra confetti
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.STREAK_MILESTONE, () => {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      })
    );
    
    // Level up - confetti + update state
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.LEVEL_UP, (data) => {
        setUserLevel(data.level);
        setGuideIntensity(rhieEngine.getGuideIntensity());
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 4000);
      })
    );
    
    // Badge earned - confetti
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.BADGE_EARNED, () => {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      })
    );
    
    // Daily ritual assigned
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.DAILY_RITUAL_ASSIGNED, (data) => {
        setDailyRitual(data.ritual);
      })
    );
    
    // Drop risk recovered - mini confetti
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.DROP_RISK_RECOVERED, () => {
        setDropRiskScore(rhieEngine.getDropRiskScore());
        setDropRiskLevel(rhieEngine.getDropRiskLevel());
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2500);
      })
    );
    
    // Drop risk changes
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.DROP_RISK_HIGH, () => {
        setDropRiskScore(rhieEngine.getDropRiskScore());
        setDropRiskLevel('HIGH');
      })
    );
    
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.DROP_RISK_CRITICAL, () => {
        setDropRiskScore(rhieEngine.getDropRiskScore());
        setDropRiskLevel('CRITICAL');
      })
    );
    
    // Hint shown - show as toast
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.HINT_SHOWN, (data) => {
        if (data.hint) {
          setToast({
            title: data.hint.title,
            body: data.hint.body,
            duration: data.hint.behavior?.autoDismissMs || 4000
          });
        }
      })
    );
    
    // Guide intensity changed
    unsubscribes.push(
      rhieEventBus.on(RHIE_EVENTS.GUIDE_INTENSITY_CHANGED, (data) => {
        setGuideIntensity(data.intensity);
      })
    );
    
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, []);
  
  // Actions
  const startRitual = useCallback((session) => {
    return rhieEngine.startRitual(session);
  }, []);
  
  const completeRitual = useCallback(async (session, actualListenedSeconds, completionPercentage) => {
    return await rhieEngine.completeRitual(session, actualListenedSeconds, completionPercentage);
  }, []);
  
  const skipRitual = useCallback((session, reason) => {
    rhieEngine.skipRitual(session, reason);
  }, []);
  
  const setDailyRitualOverride = useCallback((session) => {
    rhieEngine.setDailyRitual(session);
  }, []);
  
  const refreshData = useCallback(async () => {
    if (token) {
      await Promise.all([
        rhieEngine.fetchDailyRitual(),
        rhieEngine.fetchStreakData(),
        rhieEngine.generateRecommendations()
      ]);
      setDailyRitual(rhieEngine.getDailyRitual());
      setCurrentStreak(rhieEngine.getCurrentStreak());
      setTodayCompleted(rhieEngine.isTodayCompleted());
      setRecommendations(rhieEngine.getPersonalizedRecommendations());
      setDropRiskScore(rhieEngine.getDropRiskScore());
      setDropRiskLevel(rhieEngine.getDropRiskLevel());
    }
  }, [token]);
  
  const acceptRecommendation = useCallback((recommendation) => {
    rhieEngine.acceptRecommendation(recommendation);
  }, []);
  
  const rejectRecommendation = useCallback((recommendation) => {
    rhieEngine.rejectRecommendation(recommendation);
    setRecommendations(rhieEngine.getPersonalizedRecommendations());
  }, []);
  
  const getWinbackSuggestion = useCallback(() => {
    return rhieEngine.getWinbackSuggestion();
  }, []);
  
  const value = {
    // State
    initialized,
    dailyRitual,
    currentStreak,
    todayCompleted,
    dropRiskScore,
    dropRiskLevel,
    recommendations,
    userLevel,
    guideIntensity,
    aiSuggestion: rhieEngine.getAISuggestion(),
    
    // Actions
    startRitual,
    completeRitual,
    skipRitual,
    setDailyRitualOverride,
    refreshData,
    acceptRecommendation,
    rejectRecommendation,
    getWinbackSuggestion,
    
    // Engine reference
    engine: rhieEngine
  };
  
  return (
    <RHIEContext.Provider value={value}>
      {children}
      
      {/* Toast notifications */}
      {toast && (
        <RHIEToast
          title={toast.title}
          body={toast.body}
          duration={toast.duration}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Confetti effect */}
      {showConfetti && <RHIEConfetti />}
    </RHIEContext.Provider>
  );
}

export function useRHIE() {
  const context = useContext(RHIEContext);
  if (!context) {
    // Return safe defaults when outside provider or not initialized
    return {
      initialized: false,
      dailyRitual: null,
      currentStreak: 0,
      todayCompleted: false,
      dropRiskScore: 0,
      userLevel: 'Farkındalık',
      aiSuggestion: null,
      startRitual: () => {},
      completeRitual: async () => ({ success: false }),
      setDailyRitualOverride: () => {},
      refreshData: async () => {},
      engine: null
    };
  }
  return context;
}

export default RHIEContext;
